/**
 * <AttendeeCardModal> - Iter 9.40 Pass I
 *
 * The universal eye-icon popup for ANY preference card on the platform.
 * Opens from: kanban cells, share pages, linked-card rows, host client list,
 * admin browsers, /me/experiences tiles, intake confirmation screens.
 *
 * Layout (matches reference Screenshot 3):
 *   1. Purple gradient hero: "ATTENDEE CARD" label, host/event title, avatar,
 *      name, Client pill, "What the venue sees" tagline, Hide Details + Can't
 *      Make It buttons.
 *   2. Contact (email + phone).
 *   3. Appointment (date, time, service, status dropdown + Reschedule).
 *   4. Client Inspiration - image grid (click → lightbox).
 *   5. All Intake Responses - dynamic from rsvp.preferences/intake_answers.
 *
 * Every field is dynamic; no hardcoded copy.
 */
import { useState } from "react";
import {
  Dialog, DialogContent,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  X,
  Eye,
  EyeOff,
  Mail,
  Phone,
  Calendar,
  Heart,
  ClipboardList,
  Sparkles,
  ImageIcon
} from "lucide-react";
import ImageLightbox from "@/components/ImageLightbox";

const STATUS_OPTIONS = [
  { value: "invited", label: "Invited" },
  { value: "preferences_shared", label: "Preferences Shared" },
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Can't Make It" },
];

function fmtDate(iso) {
  if (!iso) return "Date TBD";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}
function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export default function AttendeeCardModal({
  open,
  onOpenChange,
  rsvp,
  hostEventTitle = "Preference Card",
  hostAccent = "#7c3aed",
  hostCoverImage,                      // Iter 9.47.f - host's Cover image (Hosts.Cover); fallback to category default
  categoryCoverImage,                  // Iter 9.47.f - category default cover (Categories.Default Cover)
  heroLabel,                            // If unset, derived from category (e.g., "MAKEUP PREFERENCE CARD")
  category,                             // Iter 9.47.f - category-aware default label
  subtitleText = "What the venue sees", // Iter 9.40 Pass I - per-category
  personalNote = null,                  // Iter 9.40 Pass I - owner override
  onStatusChange,
  onReschedule,
  onCantMakeIt,
  hideHostControls = false, // true on public/share pages
}) {
  const [detailsHidden, setDetailsHidden] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  if (!rsvp) return null;
  // Iter 9.47.f - category-aware label fallback
  const categoryLabels = {
    hair: "HAIR PREFERENCE CARD",
    makeup: "MAKEUP PREFERENCE CARD",
    nail: "NAIL PREFERENCE CARD",
    nails: "NAIL PREFERENCE CARD",
    lash: "LASH PREFERENCE CARD",
    lashes: "LASH PREFERENCE CARD",
    style: "STYLE PREFERENCE CARD",
    wellness: "WELLNESS PREFERENCE CARD",
    culinary: "CULINARY PREFERENCE CARD",
    instructor: "STUDENT PREFERENCE CARD",
    instructors: "STUDENT PREFERENCE CARD",
    events: "ATTENDEE CARD",
    travel: "TRAVEL PREFERENCE CARD",
    dispensary: "MEMBER PREFERENCE CARD",
  };
  const resolvedHeroLabel = heroLabel || categoryLabels[(category || "").toLowerCase()] || "PREFERENCE CARD";
  const initials = (rsvp.name || "G").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  const inspo = rsvp.inspo_images || rsvp.inspiration_images || rsvp.inspo || [];
  const prefs = rsvp.preferences || rsvp.intake_answers || {};
  const status = rsvp.appointment_stage || rsvp.status || "invited";
  const openLightbox = (arr, i) => setLightbox({
    images: arr,
    index: i,
    __onChange: (next) => setLightbox((v) => ({ ...v, index: next })),
  });

  // Separate simple vs array fields for the Intake Responses section
  const prefEntries = Object.entries(prefs).filter(([, v]) => v != null && v !== "" && v !== "none");

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-md w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto p-0 gap-0 [&>button]:hidden"
          data-testid="attendee-card-modal"
        >
          {/* ========== HERO ==========
              Iter 9.47.f - Background uses the host's Cover image (Hosts.Cover)
              with a fallback cascade to the category default (Categories.Default Cover).
              The accent color is layered as a translucent gradient OVER the image
              so the headline + avatar stay readable on any photo. */}
          <div
            className="relative text-white px-5 pt-4 pb-5"
            style={{
              backgroundImage:
                hostCoverImage || categoryCoverImage
                  ? `linear-gradient(180deg, ${hostAccent}AA 0%, ${hostAccent}CC 55%, ${hostAccent}DD 100%), url(${hostCoverImage || categoryCoverImage})`
                  : undefined,
              background:
                hostCoverImage || categoryCoverImage
                  ? undefined
                  : `linear-gradient(180deg, ${hostAccent}CC 0%, ${hostAccent} 55%, ${hostAccent}E6 100%)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            data-testid="attendee-card-hero"
          >
            <div className="flex items-start justify-between">
              <span className="text-[10px] font-semibold tracking-[0.18em] uppercase opacity-80" data-testid="attendee-hero-label">
                {resolvedHeroLabel}
              </span>
              <button
                onClick={() => onOpenChange?.(false)}
                className="w-6 h-6 rounded-full hover:bg-white/20 flex items-center justify-center"
                data-testid="attendee-card-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <h2 className="text-lg font-bold mt-1 mb-4">{hostEventTitle}</h2>

            {personalNote && (
              <p className="text-xs italic opacity-90 mb-3 px-3 py-1.5 rounded-lg bg-white/10" data-testid="attendee-personal-note">
                "{personalNote}"
              </p>
            )}

            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full border-[3px] border-white/30 bg-white/20 overflow-hidden flex items-center justify-center">
                {rsvp.profile_image ? (
                  <img
                    src={rsvp.profile_image}
                    alt={rsvp.name}
                    className="w-full h-full object-cover cursor-zoom-in"
                    onClick={() => openLightbox([rsvp.profile_image], 0)}
                  />
                ) : (
                  <span className="text-xl font-bold">{initials}</span>
                )}
              </div>
              <h3 className="text-base font-semibold mt-2">{rsvp.name}</h3>
              <Badge className="mt-1 bg-white/20 text-white border-0 text-[10px] px-2 py-0.5">
                {rsvp.role_label || "Client"}
              </Badge>
            </div>

            <p className="text-center text-[11px] opacity-70 mt-3 mb-3" data-testid="attendee-subtitle-text">{subtitleText}</p>

            {/* Hero actions */}
            {!hideHostControls && (
              <div className="flex gap-2">
                <Button
                  onClick={() => setDetailsHidden((v) => !v)}
                  className="flex-1 bg-black hover:bg-black/80 text-white border-0"
                  data-testid="attendee-card-toggle-details"
                >
                  {detailsHidden ? <><Eye className="w-4 h-4 mr-1.5" /> Show Details</> : <><EyeOff className="w-4 h-4 mr-1.5" /> Hide Details</>}
                </Button>
                {onCantMakeIt && (
                  <Button
                    onClick={onCantMakeIt}
                    variant="outline"
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white border-white/30"
                    data-testid="attendee-card-cant-make-it"
                  >
                    Can't Make It
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* ========== DETAILS (hidable) ========== */}
          {!detailsHidden && (
            <div className="bg-white">
              {/* Contact */}
              <section className="px-6 pt-5 pb-3 border-b border-gray-100" data-testid="attendee-contact">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-4 h-4 text-gray-700" />
                  <h4 className="font-bold text-gray-900">Contact</h4>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Email</p>
                    <p className="text-sm text-gray-900 truncate">{rsvp.email || "-"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">
                      Cell Phone {!rsvp.phone && <span className="text-gray-300">(optional)</span>}
                    </p>
                    {rsvp.phone ? (
                      <p className="text-sm text-gray-900">{rsvp.phone}</p>
                    ) : (
                      <button className="text-sm text-purple-600 hover:underline" data-testid="attendee-add-phone">+ Add phone</button>
                    )}
                  </div>
                </div>
              </section>

              {/* Appointment */}
              <section className="px-6 py-4 border-b border-gray-100" data-testid="attendee-appointment">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-gray-700" />
                  <h4 className="font-bold text-gray-900">Appointment</h4>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Date</p>
                    <p className="text-sm text-gray-900">{fmtDate(rsvp.appointment_date)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Time</p>
                    <p className="text-sm text-gray-900">{fmtTime(rsvp.appointment_date) || "-"}</p>
                  </div>
                </div>
                <div className="mb-3">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Service</p>
                  <p className="text-sm font-medium text-gray-900">{rsvp.service_name || "-"}</p>
                </div>
                {!hideHostControls && (
                  <div className="flex items-center gap-2">
                    <Select value={status} onValueChange={(v) => onStatusChange?.(rsvp.rsvp_id, v)}>
                      <SelectTrigger className="flex-1 h-9" data-testid="attendee-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {onReschedule && (
                      <Button variant="outline" size="sm" onClick={() => onReschedule(rsvp)} data-testid="attendee-reschedule">
                        Reschedule
                      </Button>
                    )}
                  </div>
                )}
              </section>

              {/* Client Inspiration */}
              <section className="px-6 py-4 border-b border-gray-100" data-testid="attendee-inspo">
                <div className="flex items-center gap-2 mb-3">
                  <Heart className="w-4 h-4 text-pink-500 fill-pink-500" />
                  <h4 className="font-bold text-gray-900">Client Inspiration</h4>
                </div>
                {inspo.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {inspo.map((img, i) => {
                      const src = typeof img === "string" ? img : (img.url || img.image_url);
                      return (
                        <button
                          key={i}
                          onClick={() => openLightbox(inspo.map(x => typeof x === "string" ? x : (x.url || x.image_url)), i)}
                          className="aspect-square bg-gray-100 rounded-lg overflow-hidden hover:ring-2 hover:ring-purple-400 transition-all"
                          data-testid={`attendee-inspo-${i}`}
                        >
                          <img src={src} alt="Inspiration" className="w-full h-full object-cover" />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {[0, 1, 2].map(i => (
                      <div key={i} className="aspect-square bg-gray-50 rounded-lg flex items-center justify-center border border-gray-100">
                        <ImageIcon className="w-5 h-5 text-gray-300" />
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* All Intake Responses */}
              <section className="px-6 py-4" data-testid="attendee-intake">
                <div className="flex items-center gap-2 mb-3">
                  <ClipboardList className="w-4 h-4 text-gray-700" />
                  <h4 className="font-bold text-gray-900">All Intake Responses</h4>
                </div>
                {prefEntries.length > 0 ? (
                  <div className="space-y-2">
                    {prefEntries.map(([k, v]) => {
                      const isAllergy = /allergies|allergy|sensitiv/i.test(k);
                      const bg = isAllergy ? "bg-red-50 border-red-200 text-red-900" : "bg-gray-50 border-gray-100 text-gray-900";
                      const label = k.replace(/_/g, " ");
                      const render = Array.isArray(v) ? v.join(", ") : String(v);
                      return (
                        <div key={k} className={`px-3 py-2 rounded-lg border ${bg} text-sm`} data-testid={`attendee-intake-${k}`}>
                          <p className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-0.5">{label}</p>
                          <p>{render}</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No intake responses yet.</p>
                )}
              </section>

              {/* Linked cards quick-row */}
              {rsvp.linked_card_ids?.length > 0 && rsvp.__linkedCards?.length > 0 && (
                <section className="px-6 pb-5" data-testid="attendee-linked">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    <h4 className="font-bold text-gray-900">Linked preference cards</h4>
                  </div>
                  <div className="flex gap-2 overflow-x-auto">
                    {rsvp.__linkedCards.map((c) => (
                      <button
                        key={c.rsvp_id}
                        onClick={() => rsvp.__onOpenLinked?.(c)}
                        className="flex-shrink-0 w-32 rounded-lg border border-gray-200 hover:shadow-md p-2 text-left"
                        data-testid={`attendee-linked-${c.rsvp_id}`}
                      >
                        <div className="grid grid-cols-3 h-10 rounded overflow-hidden mb-1">
                          {(c.inspo_images || []).slice(0, 3).map((im, i) => (
                            <img key={i} src={typeof im === "string" ? im : (im.url || im.image_url)} alt="" className="w-full h-full object-cover" />
                          ))}
                        </div>
                        <p className="text-[11px] font-semibold truncate">{c.name}</p>
                        <p className="text-[9px] text-gray-500 truncate">{c.service_name}</p>
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ImageLightbox value={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}
